import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useWebSocket } from '@/context/WebSocketContext';
import { useLanguage } from '@/context/LanguageContext';
import { statsAPI } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Monitor, WifiHigh, WifiSlash, ArrowRight, Buildings, CircleNotch,
  DownloadSimple, Star, Desktop
} from '@phosphor-icons/react';

const StatCard = ({ title, value, subtitle, icon: Icon, variant = 'default', onClick }) => {
  const variants = { default: 'text-white', success: 'text-[#4ade80]', danger: 'text-[#f87171]' };
  return (
    <div className={`stat-card ${onClick ? 'cursor-pointer' : ''}`} onClick={onClick} data-testid={`stat-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <span className="text-[#52525b] text-xs uppercase tracking-wider font-medium">{title}</span>
        <Icon size={20} className={variants[variant]} weight="duotone" />
      </div>
      <p className={`font-heading text-3xl font-bold ${variants[variant]}`}>{value}</p>
      {subtitle && <p className="text-[#52525b] text-sm mt-1">{subtitle}</p>}
    </div>
  );
};

const TenantCard = ({ tenant, onClick, t }) => {
  const onlinePercent = tenant.total > 0 ? Math.round((tenant.online / tenant.total) * 100) : 0;
  return (
    <div className="stat-card cursor-pointer transition-colors" onClick={onClick} data-testid={`tenant-${tenant.code}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#1C1C1F] rounded flex items-center justify-center"><Buildings size={20} className="text-[#a1a1aa]" /></div>
          <div><h3 className="font-heading font-semibold text-white">{tenant.name}</h3><span className="tenant-badge">{tenant.code}</span></div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div><p className="text-2xl font-heading font-bold text-white">{tenant.total}</p><p className="text-[#52525b] text-xs">{t('dashboard.total')}</p></div>
        <div><p className="text-2xl font-heading font-bold text-[#4ade80]">{tenant.online}</p><p className="text-[#52525b] text-xs">{t('common.online')}</p></div>
        <div><p className="text-2xl font-heading font-bold text-[#f87171]">{tenant.offline}</p><p className="text-[#52525b] text-xs">{t('common.offline')}</p></div>
      </div>
      <div className="mt-4">
        <div className="h-1.5 bg-[#1C1C1F] rounded-full overflow-hidden"><div className="h-full bg-[#4ade80] transition-all duration-500" style={{ width: `${onlinePercent}%` }} /></div>
        <p className="text-[#52525b] text-xs mt-1">{onlinePercent}% {t('common.online').toLowerCase()}</p>
      </div>
    </div>
  );
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const { user, isSuperadmin } = useAuth();
  const { subscribe } = useWebSocket();
  const { t } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [favDevices, setFavDevices] = useState([]);

  const fetchStats = async () => {
    try { const response = await statsAPI.get(); setStats(response.data); }
    catch (error) { toast.error('Dashboard error'); }
    finally { setLoading(false); }
  };

  const fetchFavorites = useCallback(async () => {
    try {
      const res = await api.get('/favorites');
      const ids = res.data.device_ids || [];
      setFavorites(ids);
      if (ids.length > 0) {
        const devRes = await api.get('/devices', { params: { per_page: 100 } });
        setFavDevices(devRes.data.devices.filter(d => ids.includes(d.id)));
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    fetchStats();
    fetchFavorites();
    const unsubscribe = subscribe((message) => {
      if (['device_status', 'device_new', 'bulk_status_change'].includes(message.type)) fetchStats();
    });
    return unsubscribe;
  }, [subscribe, fetchFavorites]);

  if (loading) return <div className="flex items-center justify-center h-64"><CircleNotch size={32} className="animate-spin text-[#D50C2D]" /></div>;

  return (
    <div data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white tracking-tight">{t('dashboard.title')}</h1>
          <p className="text-[#a1a1aa] text-sm mt-1">{t('dashboard.welcome')}, {user?.name}</p>
        </div>
        <Link to="/devices" data-testid="view-all-devices-btn" className="btn-secondary"><span>{t('dashboard.allDevices')}</span><ArrowRight size={16} /></Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <StatCard title={t('dashboard.devices')} value={stats?.total_devices || 0} subtitle={t('dashboard.registeredDevices')} icon={Monitor} onClick={() => navigate('/devices')} />
        <StatCard title={t('common.online')} value={stats?.online_devices || 0} subtitle={t('dashboard.activeConnections')} icon={WifiHigh} variant="success" onClick={() => navigate('/devices')} />
        <StatCard title={t('common.offline')} value={stats?.offline_devices || 0} subtitle={t('dashboard.disconnected')} icon={WifiSlash} variant="danger" onClick={() => navigate('/devices')} />
      </div>

      {favDevices.length > 0 && (
        <div className="mb-8">
          <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Star size={18} className="text-yellow-400" weight="fill" /> {t('dashboard.favorites')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {favDevices.map(d => (
              <div key={d.id} onClick={() => navigate(`/devices/${d.id}`)}
                className="stat-card cursor-pointer group" data-testid={`fav-device-${d.id}`}>
                <div className="flex items-center gap-3">
                  <Desktop size={18} className="text-[#52525b] group-hover:text-[#D50C2D] transition-colors" />
                  <div className="flex-1 min-w-0">
                    <span className="text-white text-sm font-medium block truncate">{d.computer_name}</span>
                    {d.location_city && <span className="text-[#52525b] text-xs">{d.location_city}</span>}
                  </div>
                  <span className={`w-2.5 h-2.5 rounded-full ${d.is_online ? 'bg-green-400' : 'bg-red-400'}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isSuperadmin && stats?.tenant_stats?.length > 0 && (
        <div>
          <h2 className="font-heading text-lg font-semibold text-white mb-4">{t('dashboard.tenants')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {stats.tenant_stats.map(tenant => <TenantCard key={tenant.id} tenant={tenant} t={t} onClick={() => navigate(`/tenants/${tenant.id}`)} />)}
          </div>
        </div>
      )}

      <div className="mt-8 p-5 bg-[#141416] border border-white/10 rounded">
        <h3 className="font-heading font-semibold text-white mb-3">{t('dashboard.quickAccess')}</h3>
        <div className="flex flex-wrap gap-3">
          <Link to="/devices" className="btn-primary"><Monitor size={18} /><span>{t('dashboard.manageDevices')}</span></Link>
          <Link to="/map" className="btn-secondary"><span>{t('nav.map')}</span></Link>
          {isSuperadmin && <Link to="/tenants" className="btn-secondary"><Buildings size={18} /><span>{t('dashboard.manageTenants')}</span></Link>}
        </div>
      </div>

      <div className="mt-6 p-4 sm:p-5 bg-gradient-to-r from-[#D50C2D]/10 to-transparent border border-[#D50C2D]/30 rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div><h3 className="font-semibold text-white mb-1 text-sm sm:text-base">{t('dashboard.agentTitle')}</h3><p className="text-[#a1a1aa] text-xs sm:text-sm">{t('dashboard.agentDesc')}</p></div>
          <a href="/api/agent/download" download className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap text-sm py-2 px-4"><DownloadSimple size={18} /><span>{t('dashboard.agentDownload')}</span></a>
        </div>
      </div>
    </div>
  );
}
